import { useTranslation } from '@splitfiesta/i18n';
import type { LineItemsRest } from '@splitfiesta/shared';

import { onRadioGroupKeyDown } from './radioGroupKeys';

interface RestRowProps {
  label: string;
  /** Formatted. */
  amount: string;
  mode: LineItemsRest['mode'];
  /** Absent: the mode reads as plain text. */
  onChange?: (rest: LineItemsRest) => void;
  testId?: string;
}

const modeKey = (mode: LineItemsRest['mode']) =>
  mode === 'equal' ? 'lineItems.restEqually' : 'lineItems.restLikeItems';

/** The "Tax, tip & the rest" row: the amount, then how it is split. */
export function RestRow({
  label,
  amount,
  mode,
  onChange,
  testId = 'line-item-rest-row',
}: RestRowProps) {
  const { t } = useTranslation('expenses');

  return (
    <div
      data-testid={testId}
      className="mt-2 space-y-2 rounded-lg bg-gray-50 px-3 py-3 dark:bg-gray-800/60"
    >
      <div
        data-testid="line-item-rest-line"
        className="flex flex-wrap items-baseline gap-x-3 gap-y-1"
      >
        <span className="min-w-[45%] flex-1 text-base text-gray-700 dark:text-gray-300">
          {label}
        </span>
        <span
          data-testid="line-item-rest-amount"
          className="ml-auto text-base font-medium tabular-nums text-gray-900 dark:text-gray-100"
        >
          {amount}
        </span>
      </div>
      <div
        data-testid="line-item-rest-split"
        className="flex flex-wrap items-center gap-2"
      >
        {onChange ? (
          <>
            <span
              data-testid="line-item-rest-lead-in"
              className="text-sm text-gray-500 dark:text-gray-400"
            >
              {t('lineItems.restSplitIt')}
            </span>
            <div
              role="radiogroup"
              aria-label={label}
              onKeyDown={onRadioGroupKeyDown}
              className="flex flex-wrap gap-2 rounded-xl bg-gray-100 p-1 dark:bg-gray-800"
            >
              {(['proportional', 'equal'] as const).map(option => {
                const active = mode === option;
                return (
                  <button
                    key={option}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    tabIndex={active ? 0 : -1}
                    onClick={() => onChange({ mode: option })}
                    className={`min-h-11 rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                      active
                        ? 'bg-white text-primary-600 dark:bg-gray-700 dark:text-primary-300'
                        : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
                    }`}
                  >
                    {t(modeKey(option))}
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <span
            data-testid="line-item-rest-mode"
            className="text-sm text-gray-500 dark:text-gray-400"
          >
            {`${t('lineItems.restSplitIt')} – ${t(modeKey(mode))}`}
          </span>
        )}
      </div>
    </div>
  );
}

export default RestRow;
